import QRCode from 'qrcode';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client';
import { type SetupInfo, useSetPassword, useSetupInfo, useVerifySetup } from '../api/setup';
import { PrivacyNotice } from '../components/PrivacyNotice';
import { applyLanguage, LANGUAGES } from '../i18n';
import ui from '../styles/ui.module.css';
import styles from './JoinPage.module.css';
import { Brand } from '../components/Logo';
import { PasswordInput } from '../components/PasswordInput';

const MIN_PASSWORD = 12;

function apiErrorText(error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : undefined;
  return t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') });
}

/**
 * A one-time link to set a password (wireframe 15, the setup card): an account the administrator
 * made sets its password and reads the privacy notice; a reset link asks for the new password
 * only; the administrator also links an authenticator app.
 */
export function SetupPage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const info = useSetupInfo(token);

  let body;
  if (info.isPending) {
    body = <p className={ui.muted}>{t('common.loading')}</p>;
  } else if (info.isError) {
    const code = info.error instanceof ApiError ? info.error.code : undefined;
    body = (
      <>
        <p className={styles.closed} role="alert">
          {code === 'setupLinkUsed' ? t('setup.used') : code === 'setupLinkExpired' ? t('setup.expired') : t('setup.notFound')}
        </p>
        <Link to="/login">{t('setup.toLogin')}</Link>
      </>
    );
  } else {
    body = <PasswordForm token={token} info={info.data} />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Brand size={36} className={styles.brand} />
        <div className={styles.languages} role="group" aria-label={t('profile.language')}>
          {LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              lang={language}
              aria-pressed={i18n.language === language}
              className={styles.languageButton}
              onClick={() => applyLanguage(language)}
            >
              {t(`languages.${language}`)}
            </button>
          ))}
        </div>
      </header>
      <main className={styles.main}>
        <div className={`${ui.card} ${styles.card}`} data-testid="setup">
          {body}
        </div>
      </main>
    </div>
  );
}

function PasswordForm({ token, info }: { token: string; info: SetupInfo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setPassword = useSetPassword(token);
  const [password, setPasswordText] = useState('');
  const [again, setAgain] = useState('');
  const [read, setRead] = useState(false);
  const [totp, setTotp] = useState<{ secret: string; uri: string } | null>(null);
  const mismatch = again.length > 0 && password !== again;
  const ready = password.length >= MIN_PASSWORD && password === again && (!info.needsPrivacyNotice || read);

  if (totp) return <AuthenticatorStep token={token} secret={totp.secret} uri={totp.uri} />;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    setPassword.mutate(
      { password, privacyNoticeRead: read },
      {
        onSuccess: (result) => {
          if (result.totpSecret && result.totpUri) setTotp({ secret: result.totpSecret, uri: result.totpUri });
          else navigate('/', { replace: true });
        },
      },
    );
  }

  const titleKey = info.purpose === 'PASSWORD_RESET' ? 'setup.resetTitle' : info.purpose === 'ADMIN_SETUP' ? 'setup.adminTitle' : 'setup.title';
  return (
    <form onSubmit={submit} noValidate className={styles.form}>
      <div>
        <div className={styles.eyebrow}>{info.purpose === 'PASSWORD_RESET' ? t('setup.resetEyebrow') : t('setup.eyebrow')}</div>
        <h1 className={styles.title}>{t(titleKey)}</h1>
        <p className={styles.lead}>{t('setup.lead', { name: info.displayName, email: info.email })}</p>
      </div>
      <div className={styles.passwords}>
        <div className={ui.field}>
          <label htmlFor="setup-password" className={ui.label}>
            {t('join.password')}
          </label>
          <PasswordInput id="setup-password" value={password} autoComplete="new-password" onChange={(e) => setPasswordText(e.target.value)} />
        </div>
        <div className={ui.field}>
          <label htmlFor="setup-password-again" className={ui.label}>
            {t('join.passwordAgain')}
          </label>
          <PasswordInput id="setup-password-again" value={again} autoComplete="new-password" onChange={(e) => setAgain(e.target.value)} />
        </div>
      </div>
      <p className={styles.hint}>{t('join.passwordHint')}</p>
      {mismatch && (
        <p className={styles.error} role="alert">
          {t('join.mismatch')}
        </p>
      )}
      {info.needsPrivacyNotice && (
        <>
          <h2 className={styles.sectionTitle}>{t('join.yourData')}</h2>
          <PrivacyNotice />
          <label className={styles.check}>
            <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} />
            {t('join.read')}
          </label>
        </>
      )}
      {setPassword.isError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {apiErrorText(setPassword.error, t)}
        </div>
      )}
      <div className={styles.actions}>
        <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={!ready || setPassword.isPending}>
          {info.purpose === 'ADMIN_SETUP' ? t('setup.next') : t('setup.submit')}
        </button>
      </div>
    </form>
  );
}

/** The administrator links an authenticator app: scan the QR code (or type the key), then enter its code. */
function AuthenticatorStep({ token, secret, uri }: { token: string; secret: string; uri: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const verify = useVerifySetup(token);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // The code field takes the focus when this step appears, so the code can be typed at once.
  const codeInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    codeInput.current?.focus();
  }, []);

  useEffect(() => {
    let alive = true;
    void QRCode.toDataURL(uri, { margin: 1, width: 220 }).then((url) => {
      if (alive) setQr(url);
    });
    return () => {
      alive = false;
    };
  }, [uri]);

  return (
    <form
      noValidate
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        verify.mutate(code, { onSuccess: () => navigate('/admin/users', { replace: true }) });
      }}
    >
      <div>
        <div className={styles.eyebrow}>{t('setup.adminEyebrow')}</div>
        <h1 className={styles.title}>{t('setup.twoStepTitle')}</h1>
        <p className={styles.lead}>{t('setup.twoStepLead')}</p>
      </div>
      {qr && <img src={qr} width={220} height={220} alt={t('setup.qr')} className={styles.qr} />}
      <p className={ui.muted}>
        {t('setup.key')} <code className={styles.secret} data-testid="totp-secret">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
      </p>
      <label className={ui.field}>
        <span className={ui.label}>{t('setup.code')}</span>
        <input
          className={`${ui.input} ${styles.codeInput}`}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          ref={codeInput}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        />
      </label>
      {verify.isError && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {apiErrorText(verify.error, t)}
        </div>
      )}
      <div className={styles.actions}>
        <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={code.length !== 6 || verify.isPending}>
          {t('setup.verify')}
        </button>
      </div>
    </form>
  );
}
